/* Diff estruturado entre duas versões de um pack (TASK-PUB-17, ADR-0468). Apresentacional e
   data-driven (§4.18): recebe o diff (capabilities +/-, minAppVersion, canais) + labels por
   prop e renderiza. Sem I/O nem fetch — o caller (portal/admin) resolve o diff no backend. */
import { cn, StatusBadge } from '@planuze/ui';
import { ArrowRightIcon, MinusIcon, PlusIcon } from 'lucide-react';

export interface VersionDiffData {
  readonly from: string;
  readonly to: string;
  readonly capabilitiesAdded: readonly string[];
  readonly capabilitiesRemoved: readonly string[];
  readonly minAppVersionChange?: { readonly from: string; readonly to: string } | undefined;
  readonly channelsAdded: readonly string[];
  readonly channelsRemoved: readonly string[];
}

export interface VersionDiffLabels {
  readonly heading: string;
  readonly capabilities: string;
  readonly channels: string;
  readonly minAppVersion: string;
  readonly noChanges: string;
}

export interface VersionDiffProps {
  readonly diff: VersionDiffData;
  readonly labels: VersionDiffLabels;
  readonly className?: string;
}

function ChangeList({
  added,
  removed,
}: {
  readonly added: readonly string[];
  readonly removed: readonly string[];
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {added.map((item) => (
        <StatusBadge key={`+${item}`} variant="success">
          <PlusIcon className="size-3" aria-hidden="true" />
          {item}
        </StatusBadge>
      ))}
      {removed.map((item) => (
        <StatusBadge key={`-${item}`} variant="destructive">
          <MinusIcon className="size-3" aria-hidden="true" />
          {item}
        </StatusBadge>
      ))}
    </div>
  );
}

export function VersionDiff({ diff, labels, className }: Readonly<VersionDiffProps>) {
  const capsChanged = diff.capabilitiesAdded.length > 0 || diff.capabilitiesRemoved.length > 0;
  const channelsChanged = diff.channelsAdded.length > 0 || diff.channelsRemoved.length > 0;
  const empty = !capsChanged && !channelsChanged && diff.minAppVersionChange === undefined;

  return (
    <section
      className={cn('flex flex-col gap-3 rounded-lg border border-border bg-card p-4', className)}
    >
      <header className="flex items-center gap-2 text-sm font-semibold text-foreground">
        {/* check:no-raw-ui-text allow-next-line -- prefixo `v` do semver de ORIGEM do diff (par `vX → vY` no header): notação de versão */}
        <span className="font-mono">v{diff.from}</span>
        <ArrowRightIcon className="size-4 text-muted-foreground" aria-hidden="true" />
        {/* check:no-raw-ui-text allow-next-line -- prefixo `v` do semver de DESTINO do diff: notação de versão */}
        <span className="font-mono">v{diff.to}</span>
      </header>
      {empty ? (
        <p className="text-sm text-muted-foreground">{labels.noChanges}</p>
      ) : (
        <dl className="flex flex-col gap-3">
          {capsChanged && (
            <div className="flex flex-col gap-1.5">
              <dt className="text-xs font-medium text-muted-foreground">{labels.capabilities}</dt>
              <dd>
                <ChangeList added={diff.capabilitiesAdded} removed={diff.capabilitiesRemoved} />
              </dd>
            </div>
          )}
          {channelsChanged && (
            <div className="flex flex-col gap-1.5">
              <dt className="text-xs font-medium text-muted-foreground">{labels.channels}</dt>
              <dd>
                <ChangeList added={diff.channelsAdded} removed={diff.channelsRemoved} />
              </dd>
            </div>
          )}
          {diff.minAppVersionChange !== undefined && (
            <div className="flex flex-col gap-1.5">
              <dt className="text-xs font-medium text-muted-foreground">{labels.minAppVersion}</dt>
              <dd className="flex items-center gap-2 text-sm text-foreground">
                <span className="font-mono">{diff.minAppVersionChange.from}</span>
                <ArrowRightIcon className="size-3 text-muted-foreground" aria-hidden="true" />
                <span className="font-mono">{diff.minAppVersionChange.to}</span>
              </dd>
            </div>
          )}
        </dl>
      )}
    </section>
  );
}
